// Pure path-to-crumbs mapping for the iOS standalone breadcrumb bar.
// Returns an array of { key, to | null }. Empty array = breadcrumb should
// hide (we're at a top-level root and the trail would just be Home).
//
// Translation keys live under breadcrumb.* in the locales.
//
// `search` (optional URL query string, e.g. "?from=social") is used by
// the /article/:id sibling route to pick its parent crumb — the path
// itself no longer encodes which list the article belongs to.
export function pathToCrumbs(pathname, search = '') {
  if (!pathname || typeof pathname !== 'string') return []

  // Root — home crumb is implicit via the trail's home icon, so the
  // actual tab crumb is injected by MobileBreadcrumb based on the
  // active tab event (the URL is the same for all main tabs).
  if (pathname === '/' || pathname === '') return []

  // Phoenix sub-pages that got promoted from state-driven takeovers
  // to real routes.
  if (pathname === '/phases') {
    return [{ key: 'breadcrumb.phases', to: null }]
  }
  if (pathname === '/settings') {
    return [{ key: 'breadcrumb.settings', to: null }]
  }
  if (pathname === '/profile') {
    return [{ key: 'breadcrumb.profile', to: null }]
  }
  if (pathname === '/habits') {
    return [{ key: 'breadcrumb.habits', to: null }]
  }
  if (pathname === '/physique') {
    return [{ key: 'breadcrumb.physique', to: null }]
  }
  if (pathname === '/sleep') {
    return [{ key: 'breadcrumb.sleep', to: null }]
  }
  if (pathname === '/goal-pace') {
    return [{ key: 'breadcrumb.goalPace', to: null }]
  }
  if (pathname === '/health') {
    return [{ key: 'breadcrumb.health', to: null }]
  }

  // News module — list, source-health admin page, and the individual
  // article detail route. The article crumb keeps the link back to
  // the list so the user can jump back without using the OS back.
  if (pathname === '/news') {
    return [{ key: 'breadcrumb.news', to: null }]
  }
  if (pathname === '/news/health') {
    return [
      { key: 'breadcrumb.news', to: '/news' },
      { key: 'breadcrumb.newsHealth', to: null },
    ]
  }
  if (/^\/news\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.news', to: '/news' },
      { key: 'breadcrumb.article', to: null },
    ]
  }

  // Social — same UX as news, different filter scope. Article
  // detail returns to /social, not /news.
  if (pathname === '/social' || pathname === '/social/') {
    return [{ key: 'breadcrumb.social', to: null }]
  }
  if (/^\/social\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.social', to: '/social' },
      { key: 'breadcrumb.article', to: null },
    ]
  }

  // Tech — same NewsPage component, category-locked to tech.
  if (pathname === '/tech' || pathname === '/tech/') {
    return [{ key: 'breadcrumb.tech', to: null }]
  }
  if (/^\/tech\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.tech', to: '/tech' },
      { key: 'breadcrumb.article', to: null },
    ]
  }

  // Games module. PR 1 ships /games/wordle only — no separate /games
  // hub yet, so the crumb is a single self-anchor rather than a
  // games-hub parent that would 404 if tapped.
  if (pathname === '/games/wordle' || pathname === '/games/wordle/') {
    return [{ key: 'breadcrumb.gamesWordle', to: null }]
  }
  if (pathname === '/games/questions' || pathname === '/games/questions/') {
    return [{ key: 'breadcrumb.gamesQuestions', to: null }]
  }
  if (pathname === '/games/quiz' || pathname === '/games/quiz/') {
    return [{ key: 'breadcrumb.gamesQuiz', to: null }]
  }
  if (pathname === '/games/spy' || pathname === '/games/spy/') {
    return [{ key: 'breadcrumb.gamesSpy', to: null }]
  }
  if (pathname === '/games/spy/scoreboard' || pathname === '/games/spy/scoreboard/') {
    return [
      { key: 'breadcrumb.gamesSpy', to: '/games/spy' },
      { key: 'breadcrumb.scoreboard', to: null },
    ]
  }
  if (pathname === '/games/quiz/history' || pathname === '/games/quiz/history/') {
    return [
      { key: 'breadcrumb.gamesQuiz', to: '/games/quiz' },
      { key: 'breadcrumb.quizHistory', to: null },
    ]
  }

  // Sibling article route. Parent crumb is picked from ?from=… (the
  // path itself no longer carries the feed signal). Defaults to news
  // when the param is missing.
  if (/^\/article\/[^/]+$/.test(pathname)) {
    let from = 'news'
    try {
      const param = new URLSearchParams(search || '').get('from')
      if (param === 'social' || param === 'tech' || param === 'news') from = param
    } catch { /* fall through to default */ }
    const parent = from === 'social' ? { key: 'breadcrumb.social', to: '/social' }
      : from === 'tech' ? { key: 'breadcrumb.tech', to: '/tech' }
      : { key: 'breadcrumb.news', to: '/news' }
    return [parent, { key: 'breadcrumb.article', to: null }]
  }

  // YouTube — standalone page (moved out of /news in a prior PR).
  if (pathname === '/youtube' || pathname === '/youtube/') {
    return [{ key: 'breadcrumb.youtube', to: null }]
  }

  // Owner-only learning shelf.
  if (pathname === '/learning' || pathname === '/learning/') {
    return [{ key: 'breadcrumb.learning', to: null }]
  }

  // Owner-only English-learning module. Subroutes (review / vocab /
  // course) hang off /english as parent crumbs so back-nav from
  // those pages restores the dashboard.
  if (pathname === '/english' || pathname === '/english/') {
    return [{ key: 'breadcrumb.english', to: null }]
  }
  if (pathname === '/english/vocab' || pathname === '/english/vocab/') {
    return [
      { key: 'breadcrumb.english', to: '/english' },
      { key: 'breadcrumb.englishVocab', to: null },
    ]
  }
  if (pathname === '/english/review' || pathname === '/english/review/') {
    return [
      { key: 'breadcrumb.english', to: '/english' },
      { key: 'breadcrumb.englishReview', to: null },
    ]
  }
  if (pathname === '/english/course' || pathname === '/english/course/') {
    return [
      { key: 'breadcrumb.english', to: '/english' },
      { key: 'breadcrumb.englishCourse', to: null },
    ]
  }
  if (pathname === '/english/tutor' || pathname === '/english/tutor/') {
    return [
      { key: 'breadcrumb.english', to: '/english' },
      { key: 'breadcrumb.englishTutor', to: null },
    ]
  }
  if (pathname === '/english/speak' || pathname === '/english/speak/') {
    return [
      { key: 'breadcrumb.english', to: '/english' },
      { key: 'breadcrumb.englishSpeak', to: null },
    ]
  }

  // Direct messages — conversations list (/messages) and the chat
  // thread with one partner (/messages/:partnerId). The thread keeps
  // a link back up to the list.
  if (pathname === '/messages' || pathname === '/messages/') {
    return [{ key: 'breadcrumb.messages', to: null }]
  }

  // Friends graph — single page.
  if (pathname === '/friends' || pathname === '/friends/') {
    return [{ key: 'breadcrumb.friends', to: null }]
  }

  // AI life search — single page.
  if (pathname === '/search' || pathname === '/search/') {
    return [{ key: 'breadcrumb.lifeSearch', to: null }]
  }

  // People CRM — single page.
  if (pathname === '/people' || pathname === '/people/') {
    return [{ key: 'breadcrumb.people', to: null }]
  }

  // Wishlist — single page.
  if (pathname === '/wishlist' || pathname === '/wishlist/') {
    return [{ key: 'breadcrumb.wishlist', to: null }]
  }
  if (/^\/messages\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.messages', to: '/messages' },
      { key: 'breadcrumb.message', to: null },
    ]
  }

  // Reading shelf — index + add-book form + per-book detail. Both
  // sub-routes link back to /reading so back-nav lands on the shelf.
  if (pathname === '/reading' || pathname === '/reading/') {
    return [{ key: 'breadcrumb.reading', to: null }]
  }
  if (pathname === '/reading/new' || pathname === '/reading/new/') {
    return [
      { key: 'breadcrumb.reading', to: '/reading' },
      { key: 'breadcrumb.readingNew', to: null },
    ]
  }
  if (pathname === '/reading/catalog' || pathname === '/reading/catalog/') {
    return [
      { key: 'breadcrumb.reading', to: '/reading' },
      { key: 'breadcrumb.readingCatalog', to: null },
    ]
  }
  if (/^\/reading\/recap\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.reading', to: '/reading' },
      { key: 'breadcrumb.readingRecap', to: null },
    ]
  }
  if (/^\/reading\/[^/]+\/read\/?$/.test(pathname)) {
    const m = pathname.match(/^\/reading\/([^/]+)\/read\/?$/)
    const bookId = m ? m[1] : ''
    return [
      { key: 'breadcrumb.reading', to: '/reading' },
      { key: 'breadcrumb.readingBook', to: bookId ? `/reading/${bookId}` : '/reading' },
      { key: 'breadcrumb.readingRead', to: null },
    ]
  }
  if (/^\/reading\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.reading', to: '/reading' },
      { key: 'breadcrumb.readingBook', to: null },
    ]
  }

  // Moments — index + camera + archive + day detail. Camera and
  // day-detail both keep a link back to /moments so back-nav from
  // either lands on the module home.
  if (pathname === '/moments' || pathname === '/moments/') {
    return [{ key: 'breadcrumb.moments', to: null }]
  }
  if (pathname === '/moments/archive' || pathname === '/moments/archive/') {
    return [
      { key: 'breadcrumb.moments', to: '/moments' },
      { key: 'breadcrumb.momentsArchive', to: null },
    ]
  }
  if (/^\/moments\/digest\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.moments', to: '/moments' },
      { key: 'breadcrumb.momentsArchive', to: '/moments/archive' },
      { key: 'breadcrumb.momentsDigest', to: null },
    ]
  }
  if (/^\/moments\/capture\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.moments', to: '/moments' },
      { key: 'breadcrumb.momentsCapture', to: null },
    ]
  }
  if (/^\/moments\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.moments', to: '/moments' },
      { key: 'breadcrumb.momentsDay', to: null },
    ]
  }

  // Workout module — root page should still show "Workout" as a crumb
  // so the user sees they've left the main tab strip.
  if (pathname === '/workout' || pathname === '/workout/') {
    return [{ key: 'breadcrumb.workout', to: null }]
  }
  if (pathname === '/workout/new') {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.newSession', to: null },
    ]
  }
  if (pathname === '/workout/history') {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.history', to: null },
    ]
  }
  if (/^\/workout\/sessions\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.history', to: '/workout/history' },
      { key: 'breadcrumb.session', to: null },
    ]
  }
  if (pathname === '/workout/exercises') {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.library', to: null },
    ]
  }
  if (/^\/workout\/exercises\/[^/]+$/.test(pathname)) {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.library', to: '/workout/exercises' },
      { key: 'breadcrumb.exercise', to: null },
    ]
  }
  if (pathname === '/workout/templates') {
    return [
      { key: 'breadcrumb.workout', to: '/workout' },
      { key: 'breadcrumb.templates', to: null },
    ]
  }

  // Unknown route — no breadcrumb. Safer to hide than guess.
  return []
}
